
import React from "react";
import { CheckCircle, ArrowRight, Search, Euro } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/section-header";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const benefits = [
  "Understand how to find the right nursing home for your needs",
  "Compare facilities across different counties in Ireland",
  "Learn about important questions to ask during your visits",
  "Get tips for a smooth transition to nursing home care",
  "Discover what to look for in care quality and staff qualifications",
];

const stats = [
  { value: "560+", label: "Nursing homes" },
  { value: "94%", label: "Fair Deal approval rate" },
  { value: "6 chapters", label: "Step-by-step guide" },
];

export default function FairDealGuide() {
  return (
    <section className="py-16 sm:py-20 bg-grandpal-beige">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <Badge variant="secondary" className="mb-4 text-xs">
              Updated for 2026
            </Badge>
            <SectionHeader
              title="Finding a Nursing Home in Ireland 2026"
              subtitle="Navigate the nursing home selection process with our comprehensive interactive guide, written in plain English."
            />

            <ul className="space-y-3 mb-8">
              {benefits.map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-grandpal-green-dark shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover text-white font-semibold"
                size="lg"
              >
                <Link to="/guides/finding-nursing-home-ireland-2026">
                  Read the Guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-grandpal-green-dark text-grandpal-green-dark hover:bg-grandpal-green-dark/10"
                size="lg"
              >
                <Link to="/nursing-homes">
                  <Search className="mr-2 h-4 w-4" />
                  Search Homes
                </Link>
              </Button>
            </div>
          </div>

          {/* Right: visual card */}
          <div className="relative">
            <div className="absolute inset-0 -rotate-3 bg-grandpal-green-light/30 rounded-2xl transform-gpu" />
            <div className="absolute inset-0 rotate-2 bg-grandpal-orange/20 rounded-2xl transform-gpu" />
            <div className="relative bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-6">
              {/* Guide title */}
              <div className="border-b border-border pb-5">
                <p className="text-xs font-mono text-muted-foreground mb-1 uppercase tracking-wide">
                  Interactive Guide · 2026
                </p>
                <h3 className="text-xl sm:text-2xl font-bold text-grandpal-green-dark leading-tight">
                  Finding a Nursing Home in Ireland
                </h3>
                <p className="text-sm text-muted-foreground mt-2">
                  6 chapters · Live stats · 100% free — no download required
                </p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                {stats.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="text-lg sm:text-xl font-bold text-grandpal-green-dark">{s.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Chapter previews */}
              <div className="space-y-2">
                {[
                  "Understanding Care Needs",
                  "Types of Nursing Homes",
                  "The Fair Deal Scheme",
                  "Finding & Comparing Homes",
                  "Visiting & What to Look For",
                  "The Move & Transition",
                ].map((ch, i) => (
                  <div
                    key={ch}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-grandpal-beige/60"
                  >
                    <span className="text-xs font-mono text-muted-foreground w-5 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-medium text-foreground">{ch}</span>
                    <ArrowRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                ))}
              </div>

              <Button asChild className="w-full bg-grandpal-green-dark hover:bg-grandpal-green-darkHover text-white">
                <Link to="/guides/finding-nursing-home-ireland-2026">
                  Read Free Guide
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
